/**
 * PortalKapisi.js — firma portalı rotalarının ikinci kapısı (backlog #311, 2026-10-11).
 *
 * AuthProtected "bu sayfa firma tarafı için mi" sorusunu cevapladıktan sonra burası sorar:
 *   1. Portal bağlamı geldi mi? Gelmediyse HATA — firma çözülemeyen bir hesap hiçbir zaman test
 *      dekoratörünün (49858) portalını görmez (eskiden FALLBACK_FIRMA_ID'ye düşüyordu).
 *   2. Hesap bir firmaya bağlı mı?
 *   3. Rotanın modülü, aktif arayüz setinde NONE değil mi (ya da `min` seviyede mi)? Değilse, başka bir
 *      sette açıksa o seti önerir (başlıktaki rol değiştirici).
 * Backend aynı kuralı uçlarda uygular (#315); burası yalnızca sayfayı seçer.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, CardBody, Container, Spinner } from 'reactstrap';
import { Lock, AlertTriangle } from 'lucide-react';
import VerticalLayout from '../Layouts/index';
import { usePortal } from '../Components/Contexts/PortalContext';
import { enAz, SEVIYE } from '../services/portalService';
import { useTranslation } from "react-i18next";

const Kutu = ({ ikon, baslik, children }) => (
  <VerticalLayout>
    <div className="page-content">
      <Container fluid>
        <Card className="border-0 shadow-sm mt-3">
          <CardBody className="text-center py-5">
            {ikon}
            <h5 className="mb-1">{baslik}</h5>
            {children}
          </CardBody>
        </Card>
      </Container>
    </div>
  </VerticalLayout>
);

/**
 * @param modul  PortalService/PortalConstant modül anahtarı; verilmezse yalnızca firma kontrolü yapılır
 * @param min    gereken seviye (varsayılan READ)
 * @param alan   #446: organizatör ALANI (STANDLAR, FIRMALAR, ...): o alanda İzleme ya da Denetim kademesi gerekir; SAKA açmadıysa neden söylenir
 */
// WAS (until #446): export default function PortalKapisi({ modul, min = SEVIYE.READ, children }) {
export default function PortalKapisi({ modul, min = SEVIYE.READ, alan, children }) {
  const { t } = useTranslation("components");
  const portal = usePortal();
  if (!portal) return children;   // personel: bu kapı onlar için değil

  if (portal.yukleniyor && !portal.veri) {
    return (
      <Kutu ikon={<Spinner color="primary" className="mb-2" />} baslik={t("portalKapisi.firmaPortaliYukleniyor")} />
    );
  }
  if (portal.hata) {
    return (
      <Kutu ikon={<AlertTriangle size={40} className="text-danger mb-2" />} baslik={t("portalKapisi.firmaBilgisiAlinamadi")}>
        <Alert color="danger" className="d-inline-block small mt-2">{portal.hata}</Alert>
        <div>
          <Button color="primary" size="sm" onClick={portal.yenile}>{t("portalKapisi.tekrarDene")}</Button>
        </div>
      </Kutu>
    );
  }
  if (!portal.firmaId) {
    return (
      <Kutu ikon={<Lock size={40} className="text-muted mb-2" />} baslik={t("portalKapisi.hesabinizBirFirmayaBagliDegil")}>
        <p className="text-muted small mb-0">
          {portal.uyari || t("portalKapisi.sakaPersoneliYetkiYonetimiNden")}
        </p>
      </Kutu>
    );
  }
  if (modul && !enAz(portal.seviye(modul), min)) {
    const baska = portal.arayuzler.find((a) => a.kod !== portal.aktif?.kod && enAz(a.moduller?.[modul], min));
    return (
      <Kutu ikon={<Lock size={40} className="text-muted mb-2" />} baslik={t("portalKapisi.buBolumBuArayuzdeYok")}>
        <p className="text-muted small mb-3">
          {portal.aktif
            ? t("portalKapisi.arayuzuBuBolumuIcermiyor", { ad: portal.aktif.ad })
            : portal.uyari || t("portalKapisi.firmanizSeciliFuardaKayitliDegil")}
        </p>
        {/* #418: the reason this module is closed (the company's set, the login's role, or a SAKA restriction) */}
        {portal.ipucu && portal.ipucu(modul, min) && (
          <p className="small mb-3" data-testid="portal-kapisi-gerekce">{portal.ipucu(modul, min)}</p>
        )}
        {baska && (
          <Button color="primary" size="sm" className="me-2" onClick={() => portal.setArayuz(baska.kod)}>
            {t("portalKapisi.arayuzuneGec", { ad: baska.ad })}
          </Button>
        )}
        <Link to="/Firma/Anasayfa" className="btn btn-outline-secondary btn-sm">{t("portalKapisi.anaSayfayaDon")}</Link>
      </Kutu>
    );
  }
  if (alan && !portal.alan?.(alan)?.gorebilir) {
    return (
      <Kutu ikon={<Lock size={40} className="text-muted mb-2" />} baslik={t("portalKapisi.alanKapali")}>
        <p className="text-muted small mb-3" data-testid="portal-kapisi-alan">
          {t("portalKapisi.alanKapaliAciklama", { alan: t(`portalKapisi.alanAdi.${alan}`) })}
        </p>
        <Link to="/Firma/Anasayfa" className="btn btn-outline-secondary btn-sm">{t("portalKapisi.anaSayfayaDon")}</Link>
      </Kutu>
    );
  }
  return children;
}
